import api from './api';
import type {
  CommissionCalculation,
  CommissionSharingPolicy,
  PropertyCommissionOverride,
  CostOfSales,
  AgentCommissionPerformance,
  AgencyCommissionPerformance,
  CreateCommissionSharingPolicyRequest,
  CreateSalesExpenseRequest,
  UpdateSalesExpenseRequest,
  CostItemTemplate,
  CreateCostItemTemplateRequest,
  UpdateCostItemTemplateRequest,
  CommissionStatus,
} from '../types/commission';

/**
 * Calculate commission for a property
 */
export const calculatePropertyCommission = async (
  agencySlug: string,
  propertyId: string
): Promise<CommissionCalculation> => {
  const res = await api.post<CommissionCalculation>(
    `/agencies/${agencySlug}/properties/${propertyId}/commission/calculate`
  );
  return res.data;
};

/**
 * Get commission calculation for a property
 */
export const getPropertyCommission = async (
  agencySlug: string,
  propertyId: string
): Promise<CommissionCalculation> => {
  const res = await api.get<CommissionCalculation>(
    `/agencies/${agencySlug}/properties/${propertyId}/commission`
  );
  return res.data;
};

/**
 * Update commission status (agency admin only)
 */
export const updateCommissionStatus = async (
  agencySlug: string,
  propertyId: string,
  status: CommissionStatus
): Promise<CommissionCalculation> => {
  const res = await api.patch<CommissionCalculation>(
    `/agencies/${agencySlug}/properties/${propertyId}/commission`,
    { status }
  );
  return res.data;
};

/**
 * Bulk update commission status (agency admin only). When setting to 'paid', records paidAt/paidBy.
 */
export const bulkUpdateCommissionStatus = async (
  agencySlug: string,
  propertyIds: string[],
  status: CommissionStatus
): Promise<{ updated: number; failed: string[] }> => {
  const res = await api.patch<{ updated: number; failed: string[] }>(
    `/agencies/${agencySlug}/commission/bulk-status`,
    { propertyIds, status }
  );
  return res.data;
};

/**
 * Get commission performance for an agent
 */
export const getAgentCommissionPerformance = async (
  agencySlug: string,
  agentId: string,
  startDate?: string,
  endDate?: string
): Promise<AgentCommissionPerformance> => {
  const params = new URLSearchParams();
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);
  const queryString = params.toString();
  const res = await api.get<AgentCommissionPerformance>(
    `/agencies/${agencySlug}/agents/${agentId}/commission-performance${queryString ? `?${queryString}` : ''}`
  );
  return res.data;
};

/**
 * Get commission summary for current user (dashboard widget): pipeline amount + paid this month.
 */
export const getCommissionSummary = async (
  agencySlug: string
): Promise<{ pipelineAmount: number; paidThisMonth: number }> => {
  const res = await api.get<{ pipelineAmount: number; paidThisMonth: number }>(
    `/agencies/${agencySlug}/commission-summary`
  );
  return res.data;
};

/**
 * Send commission digest email ("You have X in pipeline, Y paid this month") to current user or to a specific agent (admin only).
 */
export const sendCommissionDigest = async (
  agencySlug: string,
  agentId?: string
): Promise<void> => {
  await api.post(`/agencies/${agencySlug}/commission/send-digest`, agentId ? { agentId } : {});
};

/**
 * Get commission performance for the whole agency
 */
export const getAgencyCommissionPerformance = async (
  agencySlug: string,
  startDate?: string,
  endDate?: string
): Promise<AgencyCommissionPerformance> => {
  const params = new URLSearchParams();
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);
  const queryString = params.toString();
  const res = await api.get<AgencyCommissionPerformance>(
    `/agencies/${agencySlug}/commission-performance${queryString ? `?${queryString}` : ''}`
  );
  return res.data;
};

/**
 * Get commission sharing policy
 */
export const getCommissionSharingPolicy = async (
  agencySlug: string,
  agentId?: string
): Promise<CommissionSharingPolicy> => {
  const params = agentId ? `?agentId=${agentId}` : '';
  const res = await api.get<CommissionSharingPolicy>(
    `/agencies/${agencySlug}/commission-sharing-policy${params}`
  );
  return res.data;
};

/**
 * Create or update commission sharing policy
 */
export const upsertCommissionSharingPolicy = async (
  agencySlug: string,
  policy: CreateCommissionSharingPolicyRequest
): Promise<CommissionSharingPolicy> => {
  const res = await api.post<CommissionSharingPolicy>(
    `/agencies/${agencySlug}/commission-sharing-policy`,
    policy
  );
  return res.data;
};

/**
 * Get all commission sharing policies for an agency
 */
export const getAllCommissionSharingPolicies = async (
  agencySlug: string
): Promise<CommissionSharingPolicy[]> => {
  const res = await api.get<CommissionSharingPolicy[]>(
    `/agencies/${agencySlug}/commission-sharing-policies`
  );
  return res.data;
};

// ========== Property commission override (rate) ==========

/** Get property-specific commission rate override. 404 when none. */
export const getPropertyCommissionOverride = async (
  agencySlug: string,
  propertyId: string
): Promise<PropertyCommissionOverride | null> => {
  try {
    const res = await api.get<PropertyCommissionOverride>(
      `/agencies/${agencySlug}/properties/${propertyId}/commission-override`
    );
    return res.data;
  } catch (e: any) {
    if (e.response?.status === 404) return null;
    throw e;
  }
};

/** Create or update property-specific agent-split override only. */
export const upsertPropertyCommissionOverride = async (
  agencySlug: string,
  propertyId: string,
  data: {
    agentSplitMethod: 'equal' | 'custom';
    customAgentPercentages?: { primary?: number; secondary?: number; tertiary?: number };
  }
): Promise<PropertyCommissionOverride> => {
  const res = await api.put<PropertyCommissionOverride>(
    `/agencies/${agencySlug}/properties/${propertyId}/commission-override`,
    data
  );
  return res.data;
};

/** Remove property-specific commission rate override. */
export const deletePropertyCommissionOverride = async (
  agencySlug: string,
  propertyId: string
): Promise<void> => {
  await api.delete(`/agencies/${agencySlug}/properties/${propertyId}/commission-override`);
};

/** List all property-specific overrides for the agency (admin). */
export const listPropertyCommissionOverrides = async (
  agencySlug: string
): Promise<PropertyCommissionOverride[]> => {
  const res = await api.get<PropertyCommissionOverride[]>(
    `/agencies/${agencySlug}/commission-overrides`
  );
  return res.data;
};

// ========== Cost of Sales Methods ==========

/**
 * Get cost of sales for a property
 */
export const getPropertyCostOfSales = async (
  agencySlug: string,
  propertyId: string
): Promise<CostOfSales> => {
  const res = await api.get<CostOfSales>(
    `/agencies/${agencySlug}/properties/${propertyId}/cost-of-sales`
  );
  return res.data;
};

/**
 * Add a sales expense to a property
 */
export const addPropertyExpense = async (
  agencySlug: string,
  propertyId: string,
  expense: CreateSalesExpenseRequest
): Promise<{ expenseId: string; totalCost: number }> => {
  const res = await api.post<{ expenseId: string; totalCost: number }>(
    `/agencies/${agencySlug}/properties/${propertyId}/cost-of-sales/expenses`,
    expense
  );
  return res.data;
};

/**
 * Update a sales expense
 */
export const updatePropertyExpense = async (
  agencySlug: string,
  propertyId: string,
  expenseId: string,
  updates: UpdateSalesExpenseRequest
): Promise<{ totalCost: number }> => {
  const res = await api.put<{ totalCost: number }>(
    `/agencies/${agencySlug}/properties/${propertyId}/cost-of-sales/expenses/${expenseId}`,
    updates
  );
  return res.data;
};

/**
 * Delete a sales expense
 */
export const deletePropertyExpense = async (
  agencySlug: string,
  propertyId: string,
  expenseId: string
): Promise<{ totalCost: number }> => {
  const res = await api.delete<{ totalCost: number }>(
    `/agencies/${agencySlug}/properties/${propertyId}/cost-of-sales/expenses/${expenseId}`
  );
  return res.data;
};

/**
 * Recalculate cost of sales for a property
 */
export const recalculatePropertyCostOfSales = async (
  agencySlug: string,
  propertyId: string
): Promise<{ totalCost: number }> => {
  const res = await api.post<{ totalCost: number }>(
    `/agencies/${agencySlug}/properties/${propertyId}/cost-of-sales/recalculate`
  );
  return res.data;
};

// ========== Cost Item Templates (agency admin) ==========

export const listCostItemTemplates = async (
  agencySlug: string
): Promise<CostItemTemplate[]> => {
  const res = await api.get<CostItemTemplate[]>(
    `/agencies/${agencySlug}/cost-item-templates`
  );
  return res.data;
};

export const createCostItemTemplate = async (
  agencySlug: string,
  data: CreateCostItemTemplateRequest
): Promise<CostItemTemplate> => {
  const res = await api.post<CostItemTemplate>(
    `/agencies/${agencySlug}/cost-item-templates`,
    data
  );
  return res.data;
};

export const updateCostItemTemplate = async (
  agencySlug: string,
  templateId: string,
  data: UpdateCostItemTemplateRequest
): Promise<CostItemTemplate> => {
  const res = await api.put<CostItemTemplate>(
    `/agencies/${agencySlug}/cost-item-templates/${templateId}`,
    data
  );
  return res.data;
};

export const deleteCostItemTemplate = async (
  agencySlug: string,
  templateId: string
): Promise<void> => {
  await api.delete(`/agencies/${agencySlug}/cost-item-templates/${templateId}`);
};
